import { memo, useState } from 'react'

import { restApi } from 'common/api'
import { SelectValue } from 'common/components/BaseSelect'
import ConfirmationModal from 'common/components/confirmationModal'
import Modal from 'common/components/Modal'
import SingleSelect from 'common/components/singleSelect'
import { useAppSelector } from 'common/hooks/redux'

import CsvUploadResults from './csvUploadResults/CsvUploadResults'
import InputResultsTabs from './InputResultsTabs'

import styles from './InputResultModal.module.css'

type Props = {
  isOpen: boolean
  onClose: () => void
  onSubmit: () => void
}

const modes: SelectValue[] = [
  { value: 'excel', label: 'Copy and paste from Excel' },
  { value: 'csv', label: 'Upload a .CSV file' },
]
export type Value = (typeof modes)[number]['value']

const InputResultModal: React.FC<Props> = ({ isOpen, onClose, onSubmit }) => {
  const [isExitConfirmationModalOpened, setIsExitConfirmationModalOpened] =
    useState(false)
  const [selectedMode, setSelectedMode] = useState<Value>('excel')

  const campaign = useAppSelector((state) => state.campaignStates.campaignData)
  const campaignId = campaign._id
  const campaignName = campaign.name

  return (
    <Modal
      key={`${isOpen}`}
      className={styles.modal}
      centered
      closable={false}
      maskClosable={false}
      onClose={() => setIsExitConfirmationModalOpened(true)}
      hasMargin={false}
      visible={isOpen}
      size="large"
    >
      <div className="flex flex-col justify-between items-stretch h-full bg-gold-50 p-6 text-base-700">
        <div className="text-gold-500 mb-2">Input results</div>
        <div className="font-relais text-2xl font-medium text-base-700 mb-10">
          {campaignName}
        </div>
        <h1 className="font-medium text-base">Add your split results</h1>
        <h3 className="mt-2 ">
          You can upload a .CSV file or copy and paste directly from Excel.
        </h3>
        <div className="mt-4 flex gap-8 justify-between items-center mb-10">
          <SingleSelect
            className="w-75"
            data-cy="splitResults-mode-select"
            data-testid="splitResults-mode-select"
            name="mode"
            options={modes}
            value={selectedMode}
            onChange={(val) => {
              if (val?.value) {
                setSelectedMode(val.value)
              }
            }}
          />
        </div>
        {selectedMode === 'excel' && (
          <InputResultsTabs
            campaignId={campaignId}
            onSubmit={onSubmit}
            onClose={() => setIsExitConfirmationModalOpened(true)}
          />
        )}
        {selectedMode === 'csv' && (
          <CsvUploadResults
            onChangeMode={setSelectedMode}
            campaignName={campaignName}
            campaignId={campaignId}
            onClose={() => setIsExitConfirmationModalOpened(true)}
            onUploadCsv={(csvFile) => {
              const formdata = new FormData()
              formdata.append('results', csvFile)

              return restApi
                .post(`/campaigns/${campaignId}/results-csv`, formdata, {
                  headers: {
                    'Content-Type': 'multipart/form-data',
                  },
                })
                .then(() => {
                  onSubmit()
                })
            }}
          />
        )}
      </div>
      <ConfirmationModal
        data-cy="close-inputResult-modal"
        data-testid="close-inputResult-modal"
        title="Cancel uploading your results?"
        confirmationText="Are you sure you would like to stop uploading your results and leave this page?"
        confirmButtonText="Yes please"
        cancelButtonText="Cancel"
        open={isExitConfirmationModalOpened}
        onConfirm={() => {
          setIsExitConfirmationModalOpened(false)
          onClose()
        }}
        onCancel={() => {
          setIsExitConfirmationModalOpened(false)
        }}
      />
    </Modal>
  )
}

const InputResultModalWithResetState = (props: Props) => (
  <InputResultModal key={`${props.isOpen}`} {...props} />
)

// The parent component updates every few seconds, so we need to memoize this to avoid flickering the table
export default memo(InputResultModalWithResetState)
